import React, { forwardRef, useState, useCallback, useEffect } from 'react';
import { lastEventId, Integrations, reactRouterV5Instrumentation, init, startTransaction, configureScope, getCurrentHub } from '@sentry/react';
import { CaptureConsole } from '@sentry/integrations';
import { Integrations as Integrations$1 } from '@sentry/tracing';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import { Modal, Backdrop, ButtonBase } from '@material-ui/core';
import axios from 'axios';
import { useSpring, animated } from 'react-spring/web.cjs';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : new P(function (resolve) { resolve(result.value); }).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') { return; }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = ".ErrorBoundary-module_paper__1gvam {\n  width: 400px;\n  background-color: white;\n  justify-content: center;\n}\n.ErrorBoundary-module_paper__1gvam h2 {\n  font-size: larger;\n  color: black;\n}\n.ErrorBoundary-module_paper__1gvam h4 {\n  font-size: small;\n}\n.ErrorBoundary-module_paper__1gvam hr {\n  color: black;\n  size: 2px;\n}\n\n.ErrorBoundary-module_modal__1AP_7 {\n  display: flex;\n  align-items: center;\n  justify-content: center;\n}\n\n.ErrorBoundary-module_header__DroDz {\n  background-color: navy;\n  box-sizing: border-box;\n  display: flex;\n  padding-left: 0.5rem;\n  padding-bottom: 0.5rem;\n}\n.ErrorBoundary-module_header__DroDz h2 {\n  font-size: larger;\n  color: white;\n}\n\n.ErrorBoundary-module_error__39SB6 {\n  padding: 0.5rem;\n}\n\n.ErrorBoundary-module_input__3s3NJ {\n  margin: 0.5rem 0 0 0;\n  display: grid;\n  justify-content: unset;\n  padding: 0.5rem;\n}\n\n.ErrorBoundary-module_btnArea__lZdhm {\n  display: flex;\n  justify-content: space-evenly;\n  margin: 1rem 1rem 0 0;\n  flex-direction: row;\n  padding-bottom: 0.5rem;\n}";
var css = {"paper":"ErrorBoundary-module_paper__1gvam","modal":"ErrorBoundary-module_modal__1AP_7","header":"ErrorBoundary-module_header__DroDz","error":"ErrorBoundary-module_error__39SB6","input":"ErrorBoundary-module_input__3s3NJ","btnArea":"ErrorBoundary-module_btnArea__lZdhm"};
styleInject(css_248z,{"insertAt":"top"});

const Fade = forwardRef(function Fade(props, ref) {
    const { in: open, children, onEnter, onExited } = props, other = __rest(props, ["in", "children", "onEnter", "onExited"]);
    const style = useSpring({
        from: { opacity: 0 },
        to: { opacity: open ? 1 : 0 },
        onStart: () => {
            if (open && onEnter) {
                onEnter();
            }
        },
        onRest: () => {
            if (!open && onExited) {
                onExited();
            }
        },
    });
    return (React.createElement(animated.div, Object.assign({ ref: ref, style: style }, other), children));
});

const SentryKeys = [
    {
        app: 'service-portal',
        dsn: 'https://661959936917434dbc069bd417ccceb9@o76416.ingest.sentry.io/5566084',
    },
    {
        app: 'focus',
        dsn: 'https://a5b273e9e5444500bb9dd4e167e2b84f@o76416.ingest.sentry.io/1532073',
    },
    {
        app: 'focus-qa',
        dsn: 'https://80b2f90488a54b1d93e64cbb89b317ce@o76416.ingest.sentry.io/1895711',
    },
];

const useStyles = makeStyles((theme) => createStyles({
    modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    paper: {
        backgroundColor: '#000',
        border: '2px solid #000',
        boxShadow: theme.shadows[5],
        padding: theme.spacing(2, 4, 3),
    },
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: '#fff',
    },
}));
const ErrorBoundary = (props) => {
    const newError = new ErrorEvent('');
    const classes = useStyles();
    const [eventId, setEventId] = useState('');
    const [hasError, setHasError] = useState(false);
    const [error, setError] = useState(newError);
    const [username, setUsername] = useState('');
    const [feedback, setFeedback] = useState('');
    const [sentryDsn, setSentryDsn] = useState('');
    const onPageError = useCallback((error) => {
        const eventId = lastEventId();
        setHasError(true);
        setError(error);
        setEventId(eventId === undefined ? '' : eventId);
    }, []);
    // useEffect(() => {
    // }, []);
    useEffect(() => {
        var _a;
        // Keeping focus split for sentry projects to have one QA and one Prod project.
        const application = props.application === 'focus' && props.environment === 'QA' ?
            'focus-qa' : props.application;
        const dsn = (_a = SentryKeys.find((x) => x.app === application)) === null || _a === void 0 ? void 0 : _a.dsn;
        const config = {
            dsn: dsn,
            maxBreadcrumbs: 20,
            environment: props.environment === 'Prod' ? 'Production' : props.environment,
            normalizeDepth: 4,
            release: props.version ? props.version : null,
            integrations: [
                new Integrations.Breadcrumbs({
                    console: false,
                    history: true,
                    sentry: false,
                    xhr: true,
                }),
                new CaptureConsole({
                    levels: ['error'],
                }),
                new Integrations$1.BrowserTracing({
                    tracingOrigins: ['localhost', window.location.href, props.apiURL],
                    startTransactionOnPageLoad: true,
                    startTransactionOnLocationChange: true,
                    markBackgroundTransactions: true,
                    routingInstrumentation: reactRouterV5Instrumentation(window.history),
                }),
            ],
            tracesSampleRate: 1.0,
            beforeSend(event) {
                if (event.exception) {
                    event.tags = event.tags || {};
                    event.tags['Header'] = event.request.headers[0];
                    event.tags['Transaction'] = event.transaction;
                    event.tags['ErrorType'] = 'Customer Facing';
                }
                return event;
            },
            beforeBreadcrumb(breadcrumb, hint) {
                const category = breadcrumb.category.slice(0, 3);
                if (category === 'ui.') {
                    const { target } = hint.event;
                    breadcrumb.message = target.outerHTML;
                }
                return breadcrumb;
            },
        };
        // Initialize the Sentry call with the above config.
        init(config);
        setSentryDsn(dsn);
        // }
    }, [props]);
    useEffect(() => {
        window.addEventListener('error', onPageError);
        return () => {
            window.removeEventListener('error', onPageError);
        };
    }, [onPageError]);
    const onChange = useCallback((id, value) => {
        switch (id) {
            case 'username':
                setUsername(value);
                break;
            case 'feedback':
                setFeedback(value);
                break;
        }
    }, []);
    const onReload = useCallback(() => {
        setHasError(false);
        if (props.application === 'service-portal') {
            window.location.href = '/service-portal/';
        }
        else {
            window.location.href = '/';
        }
    }, []);
    const sendFeedback = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        let url = '';
        switch (props.application) {
            case 'service-portal':
                url = 'https://sentry.io/api/0/projects/reyrey/service-portal/user-feedback/';
                break;
            case 'focus':
                url = 'https://sentry.io/api/0/projects/reyrey/focus/user-feedback/';
                break;
            case 'focus-qa':
                url = 'https://sentry.io/api/0/projects/reyrey/focus-qa/user-feedback/';
                break;
        }
        const config = {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `DSN ${sentryDsn}`,
            },
        };
        const data = {
            'event_id': eventId,
            'name': username,
            'email': 'noreply@reyrey.com',
            'comments': feedback,
        };
        const resp = yield axios.post(url, data, config);
        if (resp) {
            onReload();
        }
    }), [eventId, username, feedback, sentryDsn, onReload]);
    const feedbackDisabled = username === '' || feedback === '';
    if (hasError) {
        return (React.createElement(Modal, { className: classes.modal, open: hasError, onClose: onReload, closeAfterTransition: false, BackdropComponent: Backdrop, BackdropProps: {
                timeout: 500,
                invisible: true,
            }, onBackdropClick: onReload },
            React.createElement(Fade, { in: hasError },
                React.createElement("div", { className: css.paper },
                    React.createElement("div", { className: css.header },
                        React.createElement("h2", null, "Exception Report")),
                    React.createElement("div", { className: css.error },
                        React.createElement("h4", null, "Error Message"),
                        React.createElement("p", null, error === null || error === void 0 ? void 0 : error.message),
                        React.createElement("hr", null)),
                    React.createElement("div", { className: css.input },
                        React.createElement("h2", null, "Submit Feedback"),
                        React.createElement("hr", null),
                        React.createElement("h4", null, "Name"),
                        React.createElement("input", { type: 'text', value: username, onChange: (event) => onChange('username', event.target.value) }),
                        React.createElement("br", null),
                        React.createElement("h4", null, "Describe what happened"),
                        React.createElement("textarea", { cols: 20, rows: 10, value: feedback, onChange: (event) => onChange('feedback', event.target.value) })),
                    React.createElement("div", { className: css.btnArea },
                        React.createElement(ButtonBase, { onClick: sendFeedback, style: {
                                backgroundColor: feedbackDisabled ? 'gray' : 'limegreen',
                                color: 'white',
                                padding: '10px',
                            }, disabled: feedbackDisabled }, "Send Feedback"),
                        React.createElement(ButtonBase, { style: {
                                backgroundColor: 'navy',
                                color: 'white',
                                padding: '10px',
                            }, onClick: onReload }, "Go to HomePage"))))));
    }
    return props.children;
};
function sentryStartTransaction(name, operation) {
    const transaction = startTransaction({ name: name });
    configureScope((scope) => scope.setSpan(transaction));
    const span = transaction.startChild({
        op: operation ? operation : 'task',
        description: name,
    });
    // return span;
}
function sentryStopTransaction(span) {
    const transaction = getCurrentHub()
        .getScope()
        .getTransaction();
    if (span) {
        span.finish();
    }
    if (transaction) {
        transaction.finish();
    }
}

export { ErrorBoundary, sentryStartTransaction, sentryStopTransaction };
